import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
} from "@mui/material";
import { Form, Formik } from "formik";
import { ReactElement } from "react";
import * as Yup from "yup";

import { createLinkShortenerDomainInputSchema } from "@stw/yup-validators";

import {
  CreateLinkShortenerDomainMutationVariables,
  LinkShortenerDomainRequestType,
} from "~frontend/__generated__/graphql";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { useErrorMessageFormatter } from "~frontend/util/errors";

import { DomainRequest } from "./types";

export default function AddDomainDialog({
  open,
  onClose,
  onSubmit,
  fulfillingRequest,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    values: CreateLinkShortenerDomainMutationVariables
  ) => Promise<void>;
  fulfillingRequest: DomainRequest | null;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Domain</DialogTitle>
      <Formik
        initialValues={{
          [submitErrorField]: null,
          domain: fulfillingRequest?.domain ?? "",
          fallbackUrl: fulfillingRequest?.fallbackUrl ?? "",
        }}
        validationSchema={
          createLinkShortenerDomainInputSchema(Yup).fields.input
        }
        onSubmit={async (values, formik) => {
          try {
            await onSubmit({
              input: {
                domain: values.domain,
                fallbackUrl: values.fallbackUrl,
                fulfillsRequest: fulfillingRequest?.id ?? null,
              },
            });
            formik.resetForm();
          } catch (e) {
            formik.setErrors({
              [submitErrorField]: getErrorMessage(e),
            });
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <FormikErrorBanner />
              {fulfillingRequest && (
                <Alert severity="info">
                  <strong>{fulfillingRequest.createdBy.fullName}</strong>{" "}
                  requested to{" "}
                  <strong>
                    {fulfillingRequest.requestType ===
                    LinkShortenerDomainRequestType.New
                      ? `buy the new domain name`
                      : `import their existing domain name`}
                  </strong>{" "}
                  <strong>{fulfillingRequest.domain}</strong>.
                </Alert>
              )}
              <Typography>
                Before entering a new domain, you must already have purchased
                the domain, or confirmed with the customer that they own the
                domain. After submitting this form, we will begin setup and then
                provide instructions to configure the domain to point to our
                link shortening servers.
              </Typography>
              <FormikTextField name="domain" label="Domain" fullWidth />
              <FormikTextField
                name="fallbackUrl"
                label="Redirect URL"
                bottomLabel="A fallback URL to redirect to if someone goes to an invalid link or to the domain directly"
                fullWidth
              />
            </DialogContent>
            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="secondary"
              >
                Cancel
              </Button>

              <FormikSubmitButton
                color="primary"
                variant="contained"
                submitText="Add"
                loadingText="Adding..."
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
